import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  CardMedia,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  CameraAlt,
  Videocam,
  PhotoLibrary,
  VideoLibrary,
  CloudUpload,
  CloudDownload,
  Storage,
  Folder,
  FolderOpen,
  Image,
  Movie,
  Smartphone,
  TabletMac,
  Computer,
  Settings,
  Security,
  Lock,
  LockOpen,
  Visibility,
  VisibilityOff,
  Edit,
  Delete,
  Add,
  Download,
  Upload,
  Share,
  Search,
  FilterList,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Info,
  Schedule,
  Today,
  DateRange,
  Person,
  Group,
  Work,
  Business,
  LocationOn,
  Sync,
  SyncProblem,
  SyncDisabled,
  WifiOff,
  Wifi,
  Battery3Bar,
  BatteryAlert,
  Memory,
  SdStorage,
  Cloud,
  CloudOff,
  ExpandMore,
  PlayArrow,
  Pause,
  Stop,
  FastForward,
  FastRewind,
  Fullscreen,
  FullscreenExit,
  VolumeUp,
  VolumeOff,
  HighQuality,
  Hd,
  FourK,
} from '@mui/icons-material';
import { cameraManagementService } from '../services/cameraManagementService';

interface CameraDevice {
  id: string;
  name: string;
  deviceType: 'smartphone' | 'tablet' | 'security-camera' | 'action-camera';
  userId: string;
  userName: string;
  userRole: string;
  status: 'online' | 'offline' | 'recording' | 'standby' | 'error';
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  batteryLevel: number;
  storageUsed: number;
  storageTotal: number;
  connectionType: 'wifi' | 'cellular' | 'offline';
  signalStrength: number;
  resolution: string;
  quality: 'low' | 'medium' | 'high' | 'ultra';
  isRecording: boolean;
  recordingStartTime?: string;
  autoUpload: boolean;
  encryptionEnabled: boolean;
  lastSync: string;
  totalPhotos: number;
  totalVideos: number;
  workSite?: string;
  permissions: {
    canRecord: boolean;
    canUpload: boolean;
    canDelete: boolean;
    canShare: boolean;
  };
}

interface MediaFile {
  id: string;
  deviceId: string;
  deviceName: string;
  fileName: string;
  fileType: 'photo' | 'video';
  fileSize: number;
  resolution: string;
  duration?: number;
  thumbnailUrl: string;
  fullUrl: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  timestamp: string;
  uploadStatus: 'uploaded' | 'uploading' | 'pending' | 'failed';
  uploadProgress: number;
  isEncrypted: boolean;
  tags: string[];
  workSite: string;
  userId: string;
  userName: string;
  metadata: {
    camera: string;
    iso: number;
    aperture: string;
    shutterSpeed: string;
    gpsCoordinates: string;
  };
}

interface CameraSettings {
  id: string;
  deviceId: string;
  resolution: string;
  quality: 'low' | 'medium' | 'high' | 'ultra';
  autoUpload: boolean;
  uploadOnWifiOnly: boolean;
  encryptionEnabled: boolean;
  geotagging: boolean;
  timestamping: boolean;
  watermark: boolean;
  compressionLevel: number;
  maxFileSize: number;
  recordingTimeLimit: number;
  storageCleanupEnabled: boolean;
  storageCleanupDays: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`camera-management-tabpanel-${index}`}
      aria-labelledby={`camera-management-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}





const CameraManagement: React.FC = () => {
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('all');
  const [selectedDevice, setSelectedDevice] = useState<CameraDevice | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [devicesData, mediaData] = await Promise.all([
          cameraManagementService.getDevices(),
          cameraManagementService.getMediaFiles()
        ]);
        
        // Transform API data to match local interfaces
        const transformedDevices = devicesData.map(device => ({
          id: device._id,
          name: device.name,
          deviceType: device.deviceType,
          userId: device.userId,
          userName: device.userName,
          userRole: device.userRole,
          status: device.status,
          location: device.location,
          batteryLevel: device.batteryLevel,
          storageUsed: device.storageUsed,
          storageTotal: device.storageTotal,
          connectionType: device.connectionType,
          signalStrength: device.signalStrength,
          resolution: device.resolution,
          quality: device.quality,
          isRecording: device.isRecording,
          recordingStartTime: device.recordingStartTime,
          autoUpload: device.autoUpload,
          encryptionEnabled: device.encryptionEnabled,
          lastSync: device.lastSync,
          totalPhotos: device.totalPhotos,
          totalVideos: device.totalVideos,
          workSite: device.workSite,
          permissions: device.permissions
        }));
        
        const transformedMedia = mediaData.map(media => ({
          id: media._id,
          deviceId: media.deviceId,
          deviceName: '', // Would need to be populated from device data
          fileName: media.filename,
          fileType: media.type,
          fileSize: media.size,
          resolution: media.resolution,
          duration: media.duration,
          thumbnailUrl: media.thumbnailUrl || '',
          fullUrl: media.url,
          location: media.location || { latitude: 0, longitude: 0, address: '' },
          timestamp: media.timestamp,
          uploadStatus: (media.uploaded ? 'uploaded' : 'pending') as 'uploaded' | 'uploading' | 'pending' | 'failed',
          uploadProgress: media.uploaded ? 100 : 0,
          isEncrypted: false, // Would need to be determined from API
          tags: media.tags,
          workSite: '', // Would need to be populated
          userId: '', // Would need to be populated
          userName: '', // Would need to be populated
          metadata: {
            camera: '',
            iso: 0,
            aperture: '',
            shutterSpeed: '',
            gpsCoordinates: ''
          }
        }));
        
        setDevices(transformedDevices);
        setMediaFiles(transformedMedia);
      } catch (error) {
        console.error('Error fetching camera data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Ensure devices is always an array
  const devicesArray = Array.isArray(devices) ? devices : [];
  const mediaFilesArray = Array.isArray(mediaFiles) ? mediaFiles : [];

  const filteredDevices = devicesArray.filter(device => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.location.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
    const matchesType = deviceTypeFilter === 'all' || device.deviceType === deviceTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const totalDevices = devicesArray.length;
  const onlineDevices = devicesArray.filter(d => d.status === 'online' || d.status === 'recording').length;
  const recordingDevices = devicesArray.filter(d => d.isRecording).length;
  const totalStorage = devicesArray.reduce((sum, d) => sum + (d?.storageTotal || 0), 0);
  const usedStorage = devicesArray.reduce((sum, d) => sum + (d?.storageUsed || 0), 0);
  const totalPhotos = devicesArray.reduce((sum, d) => sum + (d?.totalPhotos || 0), 0);
  const totalVideos = devicesArray.reduce((sum, d) => sum + (d?.totalVideos || 0), 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'recording': return 'info';
      case 'standby': return 'warning';
      case 'offline': return 'error';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'smartphone': return <Smartphone />;
      case 'tablet': return <TabletMac />;
      case 'security-camera': return <Videocam />;
      case 'action-camera': return <CameraAlt />;
      default: return <CameraAlt />;
    }
  };

  const getConnectionIcon = (type: string) => {
    switch (type) {
      case 'wifi': return <Wifi color="success" />;
      case 'cellular': return <Smartphone color="warning" />;
      case 'offline': return <WifiOff color="error" />;
      default: return <WifiOff />;
    }
  };

  const getQualityIcon = (quality: string) => {
    switch (quality) {
      case 'ultra': return <FourK color="primary" />;
      case 'high': return <Hd color="info" />;
      case 'medium': return <HighQuality color="warning" />;
      case 'low': return <Image color="error" />;
      default: return <Image />;
    }
  };

  const getUploadStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded': return 'success';
      case 'uploading': return 'info';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatFileSize = (sizeInMB: number) => {
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(1)} GB`;
    }
    return `${sizeInMB.toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewDevice = (device: CameraDevice) => {
    setSelectedDevice(device);
    setDeviceDialogOpen(true);
  };

  const handleViewMedia = (media: MediaFile) => {
    setSelectedMedia(media);
    setMediaDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Camera Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
          >
            Bulk Upload
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setSettingsDialogOpen(true)}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ bgcolor: 'primary.main' }}
          >
            Add Device
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CameraAlt color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Devices
                </Typography>
                <Typography variant="h5">
                  {totalDevices}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Camera devices
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Videocam color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Recording
                </Typography>
                <Typography variant="h5">
                  {recordingDevices}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active recordings
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Storage color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Storage Used
                </Typography>
                <Typography variant="h5">
                  {formatFileSize(usedStorage)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of {formatFileSize(totalStorage)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PhotoLibrary color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Media Files
                </Typography>
                <Typography variant="h5">
                  {totalPhotos + totalVideos}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totalPhotos} photos, {totalVideos} videos
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Devices" icon={<CameraAlt />} />
          <Tab label="Media Library" icon={<PhotoLibrary />} />
          <Tab label="Live View" icon={<Videocam />} />
          <Tab label="Analytics" icon={<Storage />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="recording">Recording</MenuItem>
                <MenuItem value="standby">Standby</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={deviceTypeFilter}
                label="Device Type"
                onChange={(e) => setDeviceTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="smartphone">Smartphone</MenuItem>
                <MenuItem value="tablet">Tablet</MenuItem>
                <MenuItem value="security-camera">Security Camera</MenuItem>
                <MenuItem value="action-camera">Action Camera</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setDeviceTypeFilter('all');
              }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Devices Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Connection</TableCell>
                <TableCell>Storage</TableCell>
                <TableCell>Battery</TableCell>
                <TableCell>Quality</TableCell>
                <TableCell>Media Count</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDevices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getDeviceTypeIcon(device.deviceType)}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {device.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {device.deviceType.replace('-', ' ').toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {device.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {device.userRole}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={device.status}
                        color={getStatusColor(device.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {device.isRecording && (
                        <Chip
                          label="REC"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getConnectionIcon(device.connectionType)}
                      <Typography variant="body2">
                        {device.connectionType}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatFileSize(device.storageUsed)} / {formatFileSize(device.storageTotal)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(device.storageUsed / device.storageTotal) * 100}
                        color={(device.storageUsed / device.storageTotal) > 0.8 ? 'error' : 'primary'}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {device.batteryLevel <= 20 ? (
                        <BatteryAlert color="error" />
                      ) : (
                        <Battery3Bar color={device.batteryLevel <= 50 ? 'warning' : 'success'} />
                      )}
                      <Typography variant="body2">
                        {device.batteryLevel}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getQualityIcon(device.quality)}
                      <Typography variant="body2">
                        {device.resolution}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {device.totalPhotos + device.totalVideos} files
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {device.totalPhotos}📷 {device.totalVideos}🎥
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(device.lastSync)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDevice(device)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Settings">
                        <IconButton size="small">
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sync Now">
                        <IconButton size="small">
                          <Sync />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search media files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 250, mr: 2 }}
          />
          <Button variant="outlined" startIcon={<FilterList />}>
            Filters
          </Button>
        </Box>

        <ImageList variant="masonry" cols={4} gap={8}>
          {mediaFiles.map((media) => (
            <ImageListItem key={media.id}>
              <img
                src={media.thumbnailUrl}
                alt={media.fileName}
                loading="lazy"
                style={{ cursor: 'pointer' }}
                onClick={() => handleViewMedia(media)}
              />
              <ImageListItemBar
                title={media.fileName}
                subtitle={
                  <Box>
                    <Typography variant="caption" display="block">
                      {media.userName} • {formatDate(media.timestamp)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={media.fileType}
                        size="small"
                        color={media.fileType === 'photo' ? 'primary' : 'secondary'}
                      />
                      <Chip
                        label={media.uploadStatus}
                        size="small"
                        color={getUploadStatusColor(media.uploadStatus) as any}
                      />
                      {media.isEncrypted && (
                        <Lock sx={{ fontSize: 16, color: 'success.main' }} />
                      )}
                    </Box>
                  </Box>
                }
                actionIcon={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                      onClick={() => handleViewMedia(media)}
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                    >
                      <Download />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                    >
                      <Share />
                    </IconButton>
                  </Box>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Live camera feeds will be displayed here. This feature requires camera permissions and active connections.
        </Alert>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {devices.filter(d => d.status === 'online' || d.status === 'recording').map((device) => (
            <Card key={device.id} sx={{ minWidth: 300, maxWidth: 400, flex: 1 }}>
              <CardMedia
                component="div"
                sx={{
                  height: 200,
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Videocam sx={{ fontSize: 60, color: 'grey.400' }} />
                {device.isRecording && (
                  <Chip
                    label="LIVE"
                    color="error"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </CardMedia>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {device.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {device.userName} • {device.location.address}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small">
                      <PlayArrow />
                    </IconButton>
                    <IconButton size="small">
                      <Pause />
                    </IconButton>
                    <IconButton size="small">
                      <Stop />
                    </IconButton>
                    <IconButton size="small">
                      <Fullscreen />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {device.resolution} • {device.quality}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Storage Analytics
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Storage Used
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatFileSize(usedStorage)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(usedStorage / totalStorage) * 100}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {((usedStorage / totalStorage) * 100).toFixed(1)}% of {formatFileSize(totalStorage)} used
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Status
              </Typography>
              <List dense>
                {['online', 'recording', 'standby', 'offline', 'error'].map((status) => {
                  const count = devices.filter(d => d.status === status).length;
                  const percentage = (count / totalDevices * 100).toFixed(1);
                  return (
                    <ListItem key={status}>
                      <ListItemIcon>
                        <Chip
                          label=""
                          color={getStatusColor(status) as any}
                          size="small"
                          sx={{ width: 16, height: 16, minWidth: 16 }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={status.charAt(0).toUpperCase() + status.slice(1)}
                        secondary={`${count} devices (${percentage}%)`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Media Statistics
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Photos
                </Typography>
                <Typography variant="h4" color="success.main">
                  {totalPhotos.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Videos
                </Typography>
                <Typography variant="h4" color="info.main">
                  {totalVideos.toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Device Details Dialog */}
      <Dialog
        open={deviceDialogOpen}
        onClose={() => setDeviceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Device Details - {selectedDevice?.name}
        </DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Card sx={{ flex: 1, minWidth: 300 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Device Information
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        label={selectedDevice.status}
                        color={getStatusColor(selectedDevice.status) as any}
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {selectedDevice.isRecording && (
                        <Chip
                          label="Recording"
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>User:</strong> {selectedDevice.userName} ({selectedDevice.userRole})
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Location:</strong> {selectedDevice.location.address}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Work Site:</strong> {selectedDevice.workSite}
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ flex: 1, minWidth: 300 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Technical Details
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Resolution:</strong> {selectedDevice.resolution}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Quality:</strong> {selectedDevice.quality}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Connection:</strong> {selectedDevice.connectionType}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Battery:</strong> {selectedDevice.batteryLevel}%
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Storage:</strong> {formatFileSize(selectedDevice.storageUsed)} / {formatFileSize(selectedDevice.storageTotal)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Permissions & Settings
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.permissions.canRecord} />}
                      label="Can Record"
                    />
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.permissions.canUpload} />}
                      label="Can Upload"
                    />
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.permissions.canDelete} />}
                      label="Can Delete"
                    />
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.permissions.canShare} />}
                      label="Can Share"
                    />
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.autoUpload} />}
                      label="Auto Upload"
                    />
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.encryptionEnabled} />}
                      label="Encryption"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Settings />}>
            Configure
          </Button>
          <Button variant="contained" startIcon={<Sync />}>
            Sync Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Details Dialog */}
      <Dialog
        open={mediaDialogOpen}
        onClose={() => setMediaDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Media Details - {selectedMedia?.fileName}
        </DialogTitle>
        <DialogContent>
          {selectedMedia && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <img
                    src={selectedMedia.fullUrl}
                    alt={selectedMedia.fileName}
                    style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
                  />
                </Box>
                <Box sx={{ minWidth: 300 }}>
                  <Typography variant="h6" gutterBottom>
                    File Information
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>File Name:</strong> {selectedMedia.fileName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>File Size:</strong> {formatFileSize(selectedMedia.fileSize)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Resolution:</strong> {selectedMedia.resolution}
                  </Typography>
                  {selectedMedia.duration && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Duration:</strong> {formatDuration(selectedMedia.duration)}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>
                    <strong>Captured:</strong> {formatDate(selectedMedia.timestamp)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Device:</strong> {selectedMedia.deviceName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>User:</strong> {selectedMedia.userName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Location:</strong> {selectedMedia.location.address}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Upload Status
                    </Typography>
                    <Chip
                      label={selectedMedia.uploadStatus}
                      color={getUploadStatusColor(selectedMedia.uploadStatus) as any}
                      sx={{ mb: 1 }}
                    />
                    {selectedMedia.uploadStatus === 'uploading' && (
                      <LinearProgress
                        variant="determinate"
                        value={selectedMedia.uploadProgress}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedMedia.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMediaDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Download
          </Button>
          <Button variant="contained" startIcon={<Share />}>
            Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Camera Management Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Default Settings</Typography>
            
            <FormControl fullWidth>
              <InputLabel>Default Quality</InputLabel>
              <Select label="Default Quality" defaultValue="high">
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="ultra">Ultra</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Default Resolution</InputLabel>
              <Select label="Default Resolution" defaultValue="1080p">
                <MenuItem value="720p">720p</MenuItem>
                <MenuItem value="1080p">1080p</MenuItem>
                <MenuItem value="4K">4K</MenuItem>
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Upload Settings
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Auto Upload"
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Upload on WiFi Only"
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Compress Before Upload"
              />
            </Box>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Security Settings
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Enable Encryption"
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Require Authentication"
              />
              <FormControlLabel
                control={<Switch />}
                label="Watermark Images"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CameraManagement;